export type WebAudioTrackLike = {
  enabled: boolean;
  id?: string;
  kind?: string;
  label: string;
  language?: string;
};

export type WebAudioTrackListLike = EventTarget & {
  readonly length: number;
  [index: number]: WebAudioTrackLike;
  [Symbol.iterator]?: () => Iterator<WebAudioTrackLike>;
};

export type WebKitPlaybackTargetAvailability =
  | "available"
  | "unavailable"
  | "unsupported";

export type WebKitVideoElement = HTMLVideoElement & {
  audioTracks?: WebAudioTrackListLike;
  webkitCurrentPlaybackTargetIsWireless?: boolean;
  webkitShowPlaybackTargetPicker?: () => void;
};

// WebKit can release an AirPlay receiver as soon as native HLS reaches
// `ended`. Cross the queue boundary during the ingest-time de-click instead,
// while the current video element still owns the wireless route.
export const AIRPLAY_NATURAL_ADVANCE_WINDOW_MS = 70;
export const WEB_MEDIA_NATURAL_END_PAUSE_WINDOW_SECONDS = 0.15;

export function isWebMediaNaturalEndPause({
  currentTime,
  duration,
  hasNext,
  playingIntent
}: {
  currentTime: number;
  duration: number;
  hasNext: boolean;
  playingIntent: boolean;
}) {
  const remainingSeconds = duration - currentTime;
  return Boolean(
    playingIntent &&
      hasNext &&
      Number.isFinite(remainingSeconds) &&
      remainingSeconds >= 0 &&
      remainingSeconds <= WEB_MEDIA_NATURAL_END_PAUSE_WINDOW_SECONDS
  );
}

type AirPlayNaturalAdvanceState = {
  alreadyAdvancing: boolean;
  currentTime: number;
  duration: number;
  nextPrepared: boolean;
  paused: boolean;
  playbackRate: number;
  playingIntent: boolean;
  wireless: boolean;
};

export function webKitAirPlayNaturalAdvanceDelayMs({
  alreadyAdvancing,
  currentTime,
  duration,
  nextPrepared,
  paused,
  playbackRate,
  playingIntent,
  wireless
}: AirPlayNaturalAdvanceState) {
  const remainingSeconds = duration - currentTime;
  if (
    alreadyAdvancing ||
    !playingIntent ||
    paused ||
    !wireless ||
    !nextPrepared ||
    !Number.isFinite(remainingSeconds) ||
    remainingSeconds <= 0 ||
    !Number.isFinite(playbackRate) ||
    playbackRate <= 0
  ) {
    return null;
  }
  return Math.max(
    0,
    (remainingSeconds / playbackRate) * 1_000 -
      AIRPLAY_NATURAL_ADVANCE_WINDOW_MS
  );
}

export function webAudioTracksForMedia(
  mediaTracks: WebAudioTrackListLike | undefined,
  element: HTMLVideoElement | null,
  nativeHlsSourceOwned = false
) {
  const nativeTracks = (element as WebKitVideoElement | null)?.audioTracks;
  // During AirPlay, and after a directly-routed source returns to the browser,
  // the persistent element consumes native HLS rather than hls.js's dormant
  // MSE pipeline. Prefer the audible native list in either ownership state.
  if (
    (isWebKitWirelessPlaybackActive(element) || nativeHlsSourceOwned) &&
    nativeTracks &&
    nativeTracks.length > 0
  ) {
    return nativeTracks;
  }
  return mediaTracks ?? nativeTracks;
}

/**
 * Return only the track list that Safari's native AirPlay pipeline owns.
 * The local hls.js list can remain populated after WebKit routes the element,
 * so it must never satisfy a receiver-side selection command.
 */
export function webKitWirelessAudioTracks(
  element: HTMLVideoElement | null
) {
  if (!isWebKitWirelessPlaybackActive(element)) return undefined;
  return (element as WebKitVideoElement | null)?.audioTracks;
}

export function webAudioTrackArray(
  tracks: WebAudioTrackListLike | undefined
) {
  if (!tracks) return [];
  if (typeof tracks[Symbol.iterator] === "function") {
    return Array.from(tracks as Iterable<WebAudioTrackLike>);
  }
  return Array.from({ length: tracks.length }, (_, index) => tracks[index]).filter(
    (track): track is WebAudioTrackLike => Boolean(track)
  );
}

/**
 * Enable the requested native track before disabling the outgoing one.
 * WebKit/AirPlay can otherwise observe a transient "no enabled track" state
 * and leave the receiver on its manifest default.
 */
export function selectWebAudioTrack(
  tracks: WebAudioTrackListLike | undefined,
  selectedIndex: number
) {
  const trackArray = webAudioTrackArray(tracks);
  const selected = trackArray[selectedIndex];
  if (!selected) return false;
  selected.enabled = true;
  trackArray.forEach((track, index) => {
    if (index !== selectedIndex) track.enabled = false;
  });
  return true;
}

export function webKitPlaybackTargetAvailability(
  element: HTMLVideoElement | null
): WebKitPlaybackTargetAvailability {
  return typeof (element as WebKitVideoElement | null)
    ?.webkitShowPlaybackTargetPicker === "function"
    ? "available"
    : "unsupported";
}

export function isWebKitWirelessPlaybackActive(
  element: HTMLVideoElement | null
) {
  return Boolean(
    (element as WebKitVideoElement | null)
      ?.webkitCurrentPlaybackTargetIsWireless
  );
}

export function shouldReplaceRoutedWebKitAirPlaySource({
  currentGeneration,
  currentSrc,
  nextGeneration,
  nextSrc,
  routeReplacementPending,
  wireless
}: {
  currentGeneration: string | number | null;
  currentSrc: string | null;
  nextGeneration: string | number;
  nextSrc: string | null;
  routeReplacementPending: boolean;
  wireless: boolean;
}) {
  return Boolean(
    wireless &&
      nextSrc &&
      !routeReplacementPending &&
      (currentSrc !== nextSrc || currentGeneration !== nextGeneration)
  );
}

type WebMediaSourceHost = {
  src: string;
};

/**
 * Advance an already-connected WebKit AirPlay route to another standalone
 * item. The fallback sibling was selected when the route began and must not be
 * rewritten at this boundary; the persistent native element becomes the sole
 * remote source owner while local hls.js remains dormant.
 */
export function replaceConnectedWebKitAirPlaySource(
  element: HTMLVideoElement | null,
  src: string
) {
  if (!element || !isWebKitWirelessPlaybackActive(element)) return false;
  element.src = src;
  element.load();
  return true;
}

const AIRPLAY_AUDIO_TRACK_RETRY_MS = 100;
const AIRPLAY_AUDIO_TRACK_RETRY_LIMIT = 30;

export function webKitAirPlayAudioTrackRetryDelayMs({
  attempt,
  descriptorCount,
  trackCount,
  wireless
}: {
  attempt: number;
  descriptorCount: number;
  trackCount: number;
  wireless: boolean;
}) {
  return wireless &&
    descriptorCount > 0 &&
    trackCount < descriptorCount &&
    attempt < AIRPLAY_AUDIO_TRACK_RETRY_LIMIT
    ? AIRPLAY_AUDIO_TRACK_RETRY_MS
    : null;
}

export function shouldReplaceIncompleteWebKitAirPlayAudioSource({
  descriptorCount,
  trackCount,
  wireless
}: {
  descriptorCount: number;
  trackCount: number;
  wireless: boolean;
}) {
  return wireless && descriptorCount > 0 && trackCount < descriptorCount;
}

/**
 * Keep WebKit's AirPlay-compatible sibling source current without replacing
 * `video.src` or asking the element to load. This is safe both before the
 * picker opens and while Video.js prepares a later source transition.
 */
export function primeWebKitAirPlaySource(
  element: HTMLVideoElement | null,
  src: string
) {
  const airPlaySource = element?.querySelector?.(
    'source[type="application/x-mpegURL"]'
  ) as HTMLSourceElement | null | undefined;
  if (!airPlaySource) return false;
  airPlaySource.src = src;
  return true;
}

/**
 * Keep Video.js's local source state aligned while independently priming the
 * native-HLS URL Safari will hand to AirPlay. Never assign `element.src` here:
 * doing so replaces the source-list contract and can release AirPlay.
 */
export function setWebMediaSourceForPlaybackTarget(
  media: WebMediaSourceHost,
  element: HTMLVideoElement | null,
  src: string,
  airPlaySrc = src
) {
  primeWebKitAirPlaySource(element, airPlaySrc);
  media.src = src;
}

export function requestWebKitPlaybackTarget(
  element: HTMLVideoElement | null
) {
  const picker = (element as WebKitVideoElement | null)
    ?.webkitShowPlaybackTargetPicker;
  if (typeof picker !== "function") return false;
  picker.call(element);
  return true;
}
